// Learn stories in the editor (contracts/learn.md §1): what each slide layout shows and its limits, slides as
// editable drafts (every language, steps as an array), the "Translate all slides" packing (steps go out as
// `steps.0`, `steps.1`… and fold back into the array), and the publishing checks the API makes.

import { type AppLink, type ImageRef, LOCALES, type Locale } from "../api/messaging";
import { type AdminSlide, type CtaAction, PUZZLE_CATEGORIES, PUZZLE_TYPE_RE, type SlideContent, type SlideDraft, type SlideLayout } from "../api/learn";

export type SlideField = "headline" | "text" | "stat" | "source";
export interface SlideFields {
  headline: string;
  text: string;
  stat: string;
  source: string;
  steps: string[];
}

export interface LayoutSpec {
  label: string;
  /** What the app draws. */
  shows: string;
  fields: { key: SlideField; label: string; limit: number; rows?: number; placeholder?: string }[];
  steps: boolean;
  /** English fields the API needs before publishing. */
  required: (SlideField | "steps")[];
  image: "required" | "recommended" | "optional";
}

export const STEP_LIMIT = 90;
export const STEPS_MIN = 2;
export const STEPS_MAX = 4;

/** The contract's layout table. */
export const LAYOUTS: Record<SlideLayout, LayoutSpec> = {
  image: {
    label: "Image",
    shows: "A full-bleed image with a caption at the bottom.",
    fields: [{ key: "text", label: "Caption", limit: 140, rows: 2, placeholder: "e.g. Each row follows the same rule." }],
    steps: false,
    required: [],
    image: "required",
  },
  image_text: {
    label: "Image + text",
    shows: "The image on top, then a headline and text.",
    fields: [
      { key: "headline", label: "Headline", limit: 80, placeholder: "e.g. Look across, then down" },
      { key: "text", label: "Text", limit: 280, rows: 3 },
    ],
    steps: false,
    required: ["headline", "text"],
    image: "recommended",
  },
  big_stat: {
    label: "Big stat",
    shows: "A huge number or short value, with a line under it.",
    fields: [
      { key: "stat", label: "Stat", limit: 12, placeholder: "e.g. 3 s, ×2, 87%" },
      { key: "headline", label: "Headline", limit: 80 },
      { key: "text", label: "Text", limit: 200, rows: 2 },
    ],
    steps: false,
    required: ["stat", "headline"],
    image: "optional",
  },
  tip: {
    label: "Tip",
    shows: "A “Tip” label, a headline and text, with an optional image.",
    fields: [
      { key: "headline", label: "Headline", limit: 80 },
      { key: "text", label: "Text", limit: 280, rows: 3 },
    ],
    steps: false,
    required: ["headline", "text"],
    image: "optional",
  },
  steps: {
    label: "Steps",
    shows: "A headline and 2–4 numbered steps, with an optional image.",
    fields: [{ key: "headline", label: "Headline", limit: 80 }],
    steps: true,
    required: ["headline", "steps"],
    image: "optional",
  },
  quote: {
    label: "Quote",
    shows: "A big quote and its attribution.",
    fields: [
      { key: "text", label: "Quote", limit: 200, rows: 3 },
      { key: "source", label: "Attribution", limit: 80, placeholder: "e.g. Anders Ericsson" },
    ],
    steps: false,
    required: ["text"],
    image: "optional",
  },
};

/** Slide art: illustrations without text (the app draws the text, in 13 languages), portrait 4:5. */
export const SLIDE_IMAGE = { width: 1080, height: 1350 };
export const COVER_IMAGE = { width: 1080, height: 1080 };

// ---------------------------------------------------------------- slides as drafts

export interface EditSlide {
  /** React key; stable while editing. */
  key: string;
  /** The API's id, once saved. */
  id?: string;
  layout: SlideLayout;
  image: ImageRef | null;
  content: Record<Locale, SlideFields>;
}

const blankFields = (): SlideFields => ({ headline: "", text: "", stat: "", source: "", steps: [] });
let seq = 0;
const newKey = () => `slide-${++seq}-${Math.random().toString(36).slice(2, 7)}`;

export function newSlide(layout: SlideLayout): EditSlide {
  const content = Object.fromEntries(LOCALES.map((l) => [l, blankFields()])) as Record<Locale, SlideFields>;
  if (LAYOUTS[layout].steps) content.en.steps = ["", ""];
  return { key: newKey(), layout, image: null, content };
}

export function slideFromApi(s: AdminSlide): EditSlide {
  const content = Object.fromEntries(
    LOCALES.map((l) => {
      const e = s.content[l];
      return [l, { headline: e?.headline ?? "", text: e?.text ?? "", stat: e?.stat ?? "", source: e?.source ?? "", steps: e?.steps ? [...e.steps] : [] }];
    }),
  ) as Record<Locale, SlideFields>;
  return { key: newKey(), id: s.id, layout: s.layout, image: s.image, content };
}

/** A copy with a new key and no id (the API gives it one). */
export const duplicateSlide = (s: EditSlide): EditSlide => ({
  ...s,
  key: newKey(),
  id: undefined,
  content: Object.fromEntries(LOCALES.map((l) => [l, { ...s.content[l], steps: [...s.content[l].steps] }])) as Record<Locale, SlideFields>,
});

/** The fields this layout uses, trimmed, empty ones left out (they fall back to English, or aren't shown). */
function entryFor(layout: SlideLayout, f: SlideFields) {
  const spec = LAYOUTS[layout];
  const out: Record<string, string | string[]> = {};
  for (const { key } of spec.fields) {
    const v = f[key].trim();
    if (v) out[key] = v;
  }
  if (spec.steps) {
    const steps = f.steps.map((x) => x.trim()).filter(Boolean);
    if (steps.length) out.steps = steps;
  }
  return out;
}

/** The API's slide: English always, any other language with text. */
export function slideToApi(s: EditSlide): SlideDraft {
  const content: SlideContent = {};
  for (const l of LOCALES) {
    const entry = entryFor(s.layout, s.content[l]);
    if (l === "en" || Object.keys(entry).length) content[l] = entry;
  }
  return { ...(s.id ? { id: s.id } : {}), layout: s.layout, imageId: s.image?.id ?? null, content };
}

/** One language of a slide as the app shows it: each field falls back to English. */
export function resolveSlide(s: EditSlide, locale: Locale): SlideFields {
  const en = s.content.en;
  const l = s.content[locale];
  const pick = (k: SlideField) => l[k].trim() || en[k].trim();
  const steps = l.steps.some((x) => x.trim()) ? l.steps : en.steps;
  return { headline: pick("headline"), text: pick("text"), stat: pick("stat"), source: pick("source"), steps: steps.map((x) => x.trim()).filter(Boolean) };
}

/** Has this language anything of its own on this slide? */
export const slideHasLocale = (s: EditSlide, locale: Locale) => Object.keys(entryFor(s.layout, s.content[locale])).length > 0;

/** A fingerprint of every slide's English, to warn when it changed after translating. */
export const slidesEnglishKey = (slides: EditSlide[]) => JSON.stringify(slides.map((s) => [s.layout, entryFor(s.layout, s.content.en)]));

// ---------------------------------------------------------------- "Translate all slides"

/** At most this many fields per /translate call (the API's limit). */
export const TRANSLATE_MAX_FIELDS = 40;
const KEY_RE = /^s(\d+)(headline|text|stat|source|steps)(?:\.(\d+))?$/;

/**
 * Every slide's English as flat translate fields: `s<i><field>`, and `s<i>steps.<j>` for each step, with the
 * layout's limits, in calls of at most 40 fields.
 */
export function packSlides(slides: EditSlide[]): { en: Record<string, string>; limits: Record<string, number> }[] {
  const items: [string, string, number][] = [];
  slides.forEach((s, i) => {
    const spec = LAYOUTS[s.layout];
    const en = s.content.en;
    for (const f of spec.fields) if (en[f.key].trim()) items.push([`s${i}${f.key}`, en[f.key].trim(), f.limit]);
    if (spec.steps) en.steps.map((x) => x.trim()).filter(Boolean).forEach((x, j) => items.push([`s${i}steps.${j}`, x, STEP_LIMIT]));
  });
  const calls: { en: Record<string, string>; limits: Record<string, number> }[] = [];
  for (let i = 0; i < items.length; i += TRANSLATE_MAX_FIELDS) {
    const chunk = items.slice(i, i + TRANSLATE_MAX_FIELDS);
    calls.push({ en: Object.fromEntries(chunk.map(([k, v]) => [k, v])), limits: Object.fromEntries(chunk.map(([k, , n]) => [k, n])) });
  }
  return calls;
}

/**
 * Folds translated fields back into the slides: `s<i>steps.<j>` become the steps array in order. Every slide
 * that had English text gets each translated language replaced; a field the model left out stays empty (English).
 */
export function foldSlides(slides: EditSlide[], translated: Partial<Record<Locale, Record<string, string>>>): EditSlide[] {
  const next = slides.map((s) => ({ ...s, content: { ...s.content } }));
  for (const l of LOCALES) {
    const fields = translated[l];
    if (l === "en" || !fields) continue;
    const perSlide = new Map<number, { f: Partial<Record<SlideField, string>>; steps: [number, string][] }>();
    for (const [k, v] of Object.entries(fields)) {
      const m = KEY_RE.exec(k);
      if (!m) continue;
      const i = Number(m[1]);
      const got = perSlide.get(i) ?? { f: {}, steps: [] };
      if (m[2] === "steps") got.steps.push([Number(m[3] ?? 0), v]);
      else got.f[m[2] as SlideField] = v;
      perSlide.set(i, got);
    }
    for (const [i, got] of perSlide) {
      const s = next[i];
      if (!s) continue;
      s.content[l] = {
        headline: got.f.headline ?? "",
        text: got.f.text ?? "",
        stat: got.f.stat ?? "",
        source: got.f.source ?? "",
        steps: got.steps.sort((a, b) => a[0] - b[0]).map(([, v]) => v),
      };
    }
  }
  return next;
}

// ---------------------------------------------------------------- publishing checks (as the API makes them)

export function storyProblems(d: { coverImageId: string | null; slides: EditSlide[]; enCta: string; ctaAction: CtaAction; link: AppLink | null }): string[] {
  const out: string[] = [];
  if (!d.coverImageId) out.push("Add a cover image.");
  if (d.slides.length === 0) out.push("Add at least one slide.");
  d.slides.forEach((s, i) => {
    if (s.layout === "image" && !s.image) out.push(`Slide ${i + 1}: an image slide needs an image.`);
    const en = s.content.en;
    for (const f of LAYOUTS[s.layout].required) {
      const missing = f === "steps" ? en.steps.filter((x) => x.trim()).length < STEPS_MIN : !en[f].trim();
      if (missing) out.push(`Slide ${i + 1}: write the ${f === "steps" ? "2–4 steps" : f} in English.`);
    }
  });
  if (d.ctaAction !== "none" && !d.enCta.trim()) out.push("The button needs a label (content.en.cta).");
  if (d.ctaAction === "link" && !d.link) out.push("Pick where the button goes (link).");
  return out;
}

/** Why a puzzle type can't be added, or null: `category.slug` with one of the 8 puzzle categories. */
export function puzzleTypeProblem(value: string, taken: readonly string[]): string | null {
  const v = value.trim();
  if (!PUZZLE_TYPE_RE.test(v)) return "Write it as category.slug, e.g. pattern.matrix.";
  if (!(PUZZLE_CATEGORIES as readonly string[]).includes(v.split(".")[0]!)) return `The category must be one of ${PUZZLE_CATEGORIES.join(", ")}.`;
  if (taken.includes(v)) return "Already added.";
  return null;
}
